/**
 * LinkExamples.tsx
 * Comprehensive examples of HeroUI Link component
 * 
 * Showcases all HeroUI Link features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Link } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';


export const LinkExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <p className="text-text-primary">
                Visit our <Link href="/home">home page</Link> or read the <Link href="/docs">documentation</Link>.
              </p>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Link Sizes</h3>
          <Card>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <Link href="#" size="sm">Small link</Link>
                </div>
                <div>
                  <Link href="#" size="md">Medium link (default)</Link>
                </div>
                <div>
                  <Link href="#" size="lg">Large link</Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Link Colors</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Link href="#" color="foreground">Foreground</Link>
                <Link href="#" color="primary">Primary</Link>
                <Link href="#" color="secondary">Secondary</Link>
                <Link href="#" color="success">Success</Link>
                <Link href="#" color="warning">Warning</Link>
                <Link href="#" color="danger">Danger</Link>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">External Links</h3>
          <Card>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <Link href="https://heroui.com" isExternal>
                    HeroUI Documentation (external)
                  </Link>
                </div>
                <div>
                  <Link href="https://heroui.com" isExternal showAnchorIcon>
                    HeroUI with anchor icon
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Underline Variants</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <Link href="#" underline="none">No underline</Link>
                  <span className="text-text-tertiary text-sm ml-2">(default)</span>
                </div>
                <div>
                  <Link href="#" underline="hover">Hover underline</Link>
                </div>
                <div>
                  <Link href="#" underline="always">Always underlined</Link>
                </div>
                <div>
                  <Link href="#" underline="active">Active underline</Link>
                </div>
                <div>
                  <Link href="#" underline="focus">Focus underline</Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">States</h3>
          <Card>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <Link href="#">Normal link</Link>
                </div>
                <div>
                  <Link href="#" isDisabled>Disabled link</Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Navigation Examples</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <h5 className="text-text-primary font-medium mb-2">Inline navigation:</h5>
                  <nav className="flex items-center gap-4">
                    <Link href="/dashboard" color="primary">Dashboard</Link>
                    <Link href="/trades" color="foreground">Trades</Link>
                    <Link href="/analytics" color="foreground">Analytics</Link>
                    <Link href="/settings" color="foreground">Settings</Link>
                  </nav>
                </div>
                
                <Divider />
                
                <div>
                  <h5 className="text-text-primary font-medium mb-2">Breadcrumb navigation:</h5>
                  <nav className="flex items-center gap-2 text-sm">
                    <Link href="/home" size="sm" color="foreground">Home</Link>
                    <span className="text-text-tertiary">/</span>
                    <Link href="/components" size="sm" color="foreground">Components</Link>
                    <span className="text-text-tertiary">/</span>
                    <span className="text-text-primary">Link</span>
                  </nav>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">React Router Integration</h3>
          <Card>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <Link href="/playground" color="primary">
                    Navigate to Playground
                  </Link>
                  <span className="text-text-tertiary text-sm ml-2">(client-side routing)</span>
                </div>
                <div>
                  <Link href="/playground/themes" color="secondary">
                    View Theme Examples
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};